import { type WorkspaceAgentRunStatus } from '~/components/v2/components/workspace/workspace-agent-panel/types/workspaceAgentRun';

/** Ask tools whose finished group draws nothing until the next server activity. */
export const WORKSPACE_AGENT_CHOICE_TOOL_NAMES = new Set([
  'ask_binary',
  'ask_single',
  'ask_multi',
  'ask_describe',
  'ask_fields',
  'ask_stepper',
  'ask_search',
]);

export const isWorkspaceAgentChoiceToolName = (name: string): boolean => WORKSPACE_AGENT_CHOICE_TOOL_NAMES.has(name);

type IsWorkspaceAgentElicitationFollowUpHoldParams = {
  isAwaitingElicitationFollowUp: boolean;
  tools: readonly { name: string }[];
};

/**
 * The latest run is still only the choice the user just answered. The thread keeps a client
 * loader on that group until the server paints its own.
 */
export const isWorkspaceAgentElicitationFollowUpHold = ({
  isAwaitingElicitationFollowUp,
  tools,
}: IsWorkspaceAgentElicitationFollowUpHoldParams): boolean =>
  isAwaitingElicitationFollowUp && tools.length > 0 && tools.every((tool) => isWorkspaceAgentChoiceToolName(tool.name));

type IsWorkspaceAgentServerRunLoaderRowParams = {
  status: WorkspaceAgentRunStatus;
  tools: readonly { name: string }[];
};

/** An active run that already draws the server loader, rather than the choice the user just answered. */
export const isWorkspaceAgentServerRunLoaderRow = ({
  status,
  tools,
}: IsWorkspaceAgentServerRunLoaderRowParams): boolean => {
  if (status !== 'active' && status !== 'cancelling') {
    return false;
  }

  return tools.length === 0 || tools.some((tool) => !isWorkspaceAgentChoiceToolName(tool.name));
};

type FollowUpMessage = {
  id: string;
  role: string;
};

type FollowUpRunRow = {
  status?: WorkspaceAgentRunStatus;
  tools?: readonly { name: string; status?: string }[];
  type: string;
};

export type ShouldClearWorkspaceAgentElicitationFollowUpParams = {
  assistantMessageIds: ReadonlySet<string>;
  isAwaitingRunStart: boolean;
  isSending: boolean;
  /** Session list still says the agent is blocked on an answer. That lags after a click, so it is not "the turn ended". */
  isSessionAwaitingInput: boolean;
  isSessionProcessing: boolean;
  messages: readonly FollowUpMessage[];
  rows: readonly FollowUpRunRow[];
  /** The session was seen running or waiting after this answer. A missing run id alone is not that. */
  sawSessionActivity: boolean;
  streamingMessageId: string | null;
};

const isChoiceToolStillOpen = (tool: { name: string; status?: string }): boolean =>
  isWorkspaceAgentChoiceToolName(tool.name) &&
  (tool.status === undefined || tool.status === 'working' || tool.status === 'awaitingApproval');

/** The client loader stands down once the server has moved on, or the turn has finished with nothing else to show. */
export const shouldClearWorkspaceAgentElicitationFollowUp = ({
  assistantMessageIds,
  isAwaitingRunStart,
  isSending,
  isSessionAwaitingInput,
  isSessionProcessing,
  messages,
  rows,
  sawSessionActivity,
  streamingMessageId,
}: ShouldClearWorkspaceAgentElicitationFollowUpParams): boolean => {
  const hasNewAssistantMessage = messages.some(
    (message) => message.role === 'assistant' && !assistantMessageIds.has(message.id),
  );

  if (hasNewAssistantMessage || (streamingMessageId !== null && !assistantMessageIds.has(streamingMessageId))) {
    return true;
  }

  const serverLoaderVisible = rows.some((row) => {
    if (row.type !== 'runGroup' || row.status === undefined) {
      return false;
    }

    const tools = row.tools ?? [];

    // Tools already in this run, including a choice the user just answered, are not the next activity.
    if (tools.some(isChoiceToolStillOpen)) {
      return false;
    }

    return isWorkspaceAgentServerRunLoaderRow({ status: row.status, tools });
  });

  if (serverLoaderVisible) {
    return true;
  }

  const hasLiveRun = rows.some(
    (row) => row.type === 'runGroup' && (row.status === 'active' || row.status === 'cancelling'),
  );

  return (
    sawSessionActivity &&
    !hasLiveRun &&
    !isAwaitingRunStart &&
    !isSending &&
    !isSessionProcessing &&
    !isSessionAwaitingInput
  );
};
